import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { Alert, Button, Col, ColorPicker, Collapse, Form, Input, InputNumber, Modal, Row, Select, Skeleton, Space, Switch, Tag, Typography, message } from "antd";
import { AppstoreOutlined, BgColorsOutlined, CheckCircleOutlined, CopyOutlined, CreditCardOutlined, EyeOutlined, LinkOutlined, MinusCircleOutlined, PictureOutlined, PlusOutlined, PrinterOutlined, QrcodeOutlined, SaveOutlined, ShareAltOutlined, TruckOutlined, WarningOutlined } from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import { useEcommercePermissions } from "../hooks/useEcommercePermissions";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import { useMobileMenu } from "../mobile/produits/productMobileKit";
import MobileModuleHeader from "../mobile/MobileModuleHeader";
import YengaPayConfigCard from "./components/YengaPayConfigCard";
import StorefrontBanners from "./StorefrontBanners";
import StorefrontPreview, { storefrontAssetUrl } from "./StorefrontPreview";
import useStorefrontResources from "./useStorefrontResources";
import { BannerUploadEntry, mobileBannerUploadMetadata } from "./bannerThemeUtils";
import { buildStorefrontTheme, DeliveryZoneValue, hexColor, storefrontBanners, storefrontFieldSection, storefrontFingerprint, storefrontFormValues, StorefrontSection } from "./storefrontSettingsModel";
import "./EcommerceAdmin.css";
import "./storefront-settings.css";

const { Text } = Typography;
const { TextArea } = Input;
const QR_PRINT_SIGNATURE = "Powered by ELSA GESTION | www.elsagestion.com | +226 64 98 90 99";
const generateQrDataUrl = (value: string) => QRCode.toDataURL(value, { width: 420, margin: 2, errorCorrectionLevel: "M", color: { dark: "#142033", light: "#ffffff" } });
const escapeHtml = (value: unknown) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
const errorText = (error: any, fallback: string) => error?.response?.data?.error || error?.response?.data?.message || fallback;
const sections: { key: StorefrontSection; label: string; icon: React.ReactNode }[] = [
  { key: "identity", label: "Identité visuelle", icon: <BgColorsOutlined /> },
  { key: "catalogue", label: "Catalogue", icon: <AppstoreOutlined /> },
  { key: "banners", label: "Bannières", icon: <PictureOutlined /> },
  { key: "delivery", label: "Livraison & retours", icon: <TruckOutlined /> },
  { key: "social", label: "Réseaux sociaux", icon: <ShareAltOutlined /> },
  { key: "access", label: "Accès & QR", icon: <QrcodeOutlined /> },
  { key: "payment", label: "Paiement (YengaPay)", icon: <CreditCardOutlined /> },
];

// Les URL temporaires restent locales et sont libérées à chaque remplacement,
// suppression et changement de boutique ; elles ne sont jamais envoyées à l'API.
function useBannerPreviews(entries: BannerUploadEntry[]) {
  const result = useMemo(() => entries.map((entry) => ({ ...entry,
    thumbUrl: entry.originFileObj instanceof Blob ? URL.createObjectURL(entry.originFileObj) : storefrontAssetUrl(entry.url),
  })), [entries]);
  useEffect(() => () => result.forEach((entry) => {
    if (entry.originFileObj instanceof Blob && entry.thumbUrl) URL.revokeObjectURL(entry.thumbUrl);
  }), [result]);
  return result;
}

function StorefrontWorkspace({ initialStoreId }: { initialStoreId?: number }) {
  const [form] = Form.useForm();
  const [modal, modalContext] = Modal.useModal();
  const [notices, noticeContext] = message.useMessage();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const onMenu = useMobileMenu();
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const resources = useStorefrontResources(selectedStoreId);
  const { stores, settings, catalogue, permissions, headers } = resources;
  const [baseline, setBaseline] = useState<any>(null);
  const baselineRef = useRef<any>(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [conflict, setConflict] = useState(false);
  const [recoveryNeeded, setRecoveryNeeded] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mounted = useRef(true);
  const mutationAbort = useRef<AbortController | null>(null);
  const [bannerImagesList, setBannerImagesList] = useState<BannerUploadEntry[]>([]);
  const [mobileBannersList, setMobileBannersList] = useState<BannerUploadEntry[]>([]);
  const [activeSection, setActiveSection] = useState<StorefrontSection>("banners");
  const [selectedBanner, setSelectedBanner] = useState(0);
  const [qrOpen, setQrOpen] = useState(false);
  const [storeQr, setStoreQr] = useState<string | null>(null);
  const [printingQr, setPrintingQr] = useState(false);
  const [deliveryZonesExpanded, setDeliveryZonesExpanded] = useState(false);
  const [paymentOpened, setPaymentOpened] = useState(false);
  const [paymentDirty, setPaymentDirty] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  // Inclure les zones dont le détail n'a pas encore été ouvert.
  const values = Form.useWatch([], { form, preserve: true }) || {};
  const deliveryZoneValues = values.delivery_zones;
  const deliveryZoneCount = Array.isArray(deliveryZoneValues) ? deliveryZoneValues.length : 0;
  const activeDeliveryZoneCount = Array.isArray(deliveryZoneValues) ? deliveryZoneValues.filter((zone: DeliveryZoneValue) => zone.is_active !== false).length : 0;
  const selectedStore = baseline?.id === selectedStoreId ? baseline : stores.data?.find((store) => Number(store.id) === selectedStoreId);
  const selectedStoreUrl = selectedStore ? API_CONFIG.getStoreMetaUrl(selectedStore.store_slug, selectedStore.logo_url, selectedStore.domain,
    `${selectedStore.store_name || ""}:${selectedStore.store_description || ""}`) : "";
  const legacyDeliveryZones = baseline?.legacy_delivery_zones || "";
  const loaded = Boolean(baseline && Number(baseline.id) === selectedStoreId);
  const storeMissing = Boolean(selectedStoreId && stores.data && !stores.data.some((store) => Number(store.id) === selectedStoreId));
  const canEdit = permissions.canEditFrontend() && permissions.canEditStores();
  const blocked = !loaded || saving || paymentBusy || storeMissing || Boolean(settings.error) || recoveryNeeded;
  const saveReason = !canEdit ? "Modification réservée aux utilisateurs autorisés à modifier le frontend et les boutiques."
    : storeMissing ? "Cette boutique n'est plus disponible dans l'établissement."
    : conflict ? "Rechargez les paramètres modifiés depuis une autre session."
    : recoveryNeeded ? "Rechargez l'état enregistré pour poursuivre."
    : settings.error ? "Actualisez les paramètres avant d'enregistrer."
    : !loaded ? "Chargement des paramètres de la boutique."
    : saving ? "Enregistrement des paramètres en cours."
    : paymentBusy ? "L'enregistrement du paiement est en cours."
    : dirty ? "Vos modifications ne sont pas encore enregistrées." : "Aucune modification en attente.";
  const desktopPreviews = useBannerPreviews(bannerImagesList);
  const mobilePreviews = useBannerPreviews(mobileBannersList);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; mutationAbort.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!selectedStoreId && stores.data?.length) {
      const requested = stores.data.find((store) => Number(store.id) === initialStoreId);
      setSelectedStoreId(Number((requested || stores.data[0]).id));
    }
  }, [selectedStoreId, stores.data, initialStoreId]);
  const markDirty = useCallback(() => { dirtyRef.current = true; setDirty(true); }, []);
  const applyStore = useCallback((store: any) => {
    form.setFieldsValue(storefrontFormValues(store));
    const banners = storefrontBanners(store);
    setBannerImagesList(banners.desktop); setMobileBannersList(banners.mobile);
    baselineRef.current = store; setBaseline(store); dirtyRef.current = false;
    setDirty(false); setConflict(false); setRecoveryNeeded(false);
  }, [form]);
  useEffect(() => {
    if (!settings.data || savingRef.current) return;
    if (!baselineRef.current) applyStore(settings.data);
    else if (storefrontFingerprint(settings.data) !== storefrontFingerprint(baselineRef.current)) {
      if (dirtyRef.current) setConflict(true);
      else applyStore(settings.data);
    }
  }, [settings.data, applyStore]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty && !paymentDirty) return;
      event.preventDefault(); event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, paymentDirty]);
  useEffect(() => {
    let cancelled = false;
    setStoreQr(null);
    if (selectedStoreUrl) generateQrDataUrl(selectedStoreUrl).then((qr: string) => { if (!cancelled) setStoreQr(qr); }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedStoreUrl]);

  const previewStore = () => { if (selectedStoreUrl) window.open(selectedStoreUrl, "_blank", "noopener,noreferrer"); };
  const copyToClipboard = async (value: string, text: string) => {
    try { await navigator.clipboard.writeText(value); notices.success(text); }
    catch { notices.error("Copie impossible depuis ce navigateur."); }
  };
  const askDiscard = (action: () => void) => {
    if (!dirty && !paymentDirty) { action(); return; }
    modal.confirm({ title: "Abandonner les modifications non enregistrées ?",
      content: "Les champs et les images ajoutés depuis le dernier enregistrement seront abandonnés.",
      okText: "Abandonner les modifications", cancelText: "Continuer la saisie", onOk: action });
  };
  const selectStore = (id: number) => askDiscard(() => {
    if (!mounted.current || savingRef.current || paymentBusy) return;
    baselineRef.current = null; setBaseline(null); form.resetFields();
    setBannerImagesList([]); setMobileBannersList([]); setConflict(false); setRecoveryNeeded(false);
    setDirty(false); dirtyRef.current = false; setPaymentDirty(false); setPaymentOpened(false);
    setDeliveryZonesExpanded(false); setSelectedBanner(0); setSelectedStoreId(id);
  });
  const reloadStoredValues = () => askDiscard(() => {
    void settings.refresh().then((store) => { if (mounted.current) applyStore(store); })
      .catch((error) => notices.error(errorText(error, "Impossible de recharger les paramètres. Votre saisie est conservée.")));
  });
  const changeSection = (key: StorefrontSection) => {
    setActiveSection(key); if (key === "payment") setPaymentOpened(true);
  };
  const pickColor = async (fieldName: string) => {
    const Api = (window as any).EyeDropper;
    if (!Api) { notices.warning("La pipette n'est pas disponible dans ce navigateur."); return; }
    try {
      const result = await new Api().open();
      if (mounted.current) { form.setFieldValue(fieldName, result.sRGBHex); markDirty(); }
    } catch (error: any) { if (error.name !== "AbortError") notices.error("Impossible de prélever cette couleur."); }
  };
  const colorControl = (label: string, field: string, fallback: string) => <Form.Item label={label} htmlFor={field} key={field}>
    <Space.Compact block><ColorPicker value={hexColor(values[field], fallback)} disabled={!canEdit || blocked}
      onChange={(color) => { form.setFieldValue(field, color.toHexString()); markDirty(); }} />
      <Form.Item name={field} noStyle rules={[{ pattern: /^#?[\da-f]{6}$/i, message: "Saisissez une couleur hexadécimale sur 6 caractères." }]}>
        <Input id={field} aria-label={label} maxLength={7} /></Form.Item>
      <Button icon={<BgColorsOutlined />} aria-label={`Prélever : ${label}`} onClick={() => void pickColor(field)} /></Space.Compact>
  </Form.Item>;

  const handleSubmit = async () => {
    if (!canEdit || blocked || conflict || savingRef.current || !selectedStoreId) return;
    const submittedValues = form.getFieldsValue(true);
    const id = selectedStoreId;
    savingRef.current = true; setSaving(true);
    const controller = new AbortController(); mutationAbort.current = controller;
    let themeSaved = false;
    try {
      const [currentStores, currentStore] = await Promise.all([stores.refresh(), settings.refresh()]);
      if (!mounted.current || controller.signal.aborted) return;
      if (!currentStores.some((store) => Number(store.id) === id)) throw new Error("Cette boutique n'est plus disponible dans l'établissement.");
      if (storefrontFingerprint(currentStore) !== storefrontFingerprint(baselineRef.current)) {
        setConflict(true); return;
      }
      const body = new FormData();
      body.append("theme_config", JSON.stringify(buildStorefrontTheme(submittedValues, bannerImagesList, mobileBannersList, currentStore)));
      bannerImagesList.filter((entry) => entry.originFileObj && entry.status !== "done").forEach((entry, index) => {
        body.append("banners", entry.originFileObj as Blob);
        if (entry.title) body.append(`banner_title_${index}`, entry.title);
        if (entry.description) body.append(`banner_description_${index}`, entry.description);
      });
      const mobileMetadata = mobileBannerUploadMetadata(bannerImagesList, mobileBannersList);
      if (mobileMetadata.length) body.append("mobile_banners_metadata", JSON.stringify(mobileMetadata));
      mobileBannersList.filter((entry) => entry.originFileObj && entry.status !== "done").forEach((entry) => body.append("mobile_banners", entry.originFileObj as Blob));
      await apiClient.put(API_CONFIG.apiUrl(`/ecommerce/stores/${id}/theme`), body, { headers, signal: controller.signal });
      themeSaved = true;
      if (!mounted.current || controller.signal.aborted) return;
      const delivery = { delivery_policy: submittedValues.delivery_policy, return_policy: submittedValues.return_policy,
        delivery_timeframe: submittedValues.delivery_timeframe, min_order_amount: submittedValues.min_order_amount, delivery_cost_info: submittedValues.delivery_cost_info };
      const zones = (submittedValues.delivery_zones || []).map((zone: DeliveryZoneValue) => ({ ...zone, name: zone.name.trim(), delivery_fee: Number(zone.delivery_fee || 0), is_active: zone.is_active !== false }));
      const results = await Promise.allSettled([
        apiClient.put(API_CONFIG.apiUrl(`/ecommerce/stores/${id}`), delivery, { headers, signal: controller.signal }),
        apiClient.put(API_CONFIG.apiUrl(`/ecommerce/stores/${id}/delivery-zones`), { zones }, { headers, signal: controller.signal }),
      ]);
      if (!mounted.current || controller.signal.aborted) return;
      const savedStore = await settings.refresh();
      if (!mounted.current || controller.signal.aborted) return;
      const failure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (failure) {
        // Les images sont déjà enregistrées : reprendre leurs URL sans les uploader
        // une seconde fois, tout en conservant les champs de livraison en cours.
        baselineRef.current = savedStore; setBaseline(savedStore);
        const savedBanners = storefrontBanners(savedStore); setBannerImagesList(savedBanners.desktop); setMobileBannersList(savedBanners.mobile);
        notices.warning(errorText(failure.reason, "L'apparence est enregistrée, mais la livraison n'a pas pu être entièrement sauvegardée. Votre saisie est conservée."));
      } else {
        applyStore(savedStore); notices.success("Paramètres de la boutique enregistrés.");
      }
      void resources.invalidate();
    } catch (error: any) {
      if (!mounted.current || controller.signal.aborted) return;
      if (themeSaved) setRecoveryNeeded(true);
      notices.error(errorText(error, themeSaved ? "L'apparence a été enregistrée. Rechargez l'état enregistré avant une nouvelle sauvegarde." : error.message || "Impossible d'enregistrer les paramètres. Votre saisie est conservée."));
    } finally { if (mounted.current) { savingRef.current = false; setSaving(false); } }
  };

  const printStoreQr = async () => {
    if (!selectedStore || !selectedStoreUrl) {
      message.warning("Selectionnez une boutique avant d'imprimer le QR.");
      return;
    }

    setPrintingQr(true);

    try {
      const qrData = storeQr || (await generateQrDataUrl(selectedStoreUrl));
      const printWindow = window.open("", "_blank", "width=900,height=720");

      if (!printWindow) {
        message.error("La fenetre d'impression a ete bloquee.");
        return;
      }

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>QR boutique e-commerce</title>
            <style>
              * { box-sizing: border-box; }
              body {
                margin: 0;
                padding: 14mm;
                color: #142033;
                font-family: Arial, sans-serif;
                background: #fff;
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
              @page { size: A4 portrait; margin: 14mm; }
              .sheet {
                min-height: 269mm;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
              }
              .label {
                display: inline-block;
                margin-bottom: 8px;
                padding: 6px 10px;
                border-radius: 999px;
                color: #1d4ed8;
                background: #eff6ff;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.08em;
                text-transform: uppercase;
              }
              h1 {
                max-width: 170mm;
                margin: 0 0 6px;
                color: #0f172a;
                font-size: 28px;
                line-height: 1.15;
              }
              .subtitle {
                max-width: 150mm;
                margin: 0 0 12mm;
                color: #64748b;
                font-size: 14px;
                line-height: 1.45;
              }
              .qr-frame {
                padding: 8mm;
                border: 1px solid #d9e2ec;
                border-radius: 10px;
              }
              img {
                width: 116mm;
                height: 116mm;
                display: block;
              }
              .url {
                max-width: 170mm;
                margin-top: 9mm;
                color: #475569;
                font-size: 12px;
                line-height: 1.45;
                word-break: break-all;
              }
              .powered-by {
                width: 100%;
                margin-top: auto;
                padding-top: 5mm;
                border-top: 1px solid #e2e8f0;
                color: #7a8699;
                font-size: 9px;
                line-height: 1.35;
              }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            <main class="sheet">
              <span class="label">Boutique e-commerce</span>
              <h1>${escapeHtml(selectedStore.store_name || "Boutique")}</h1>
              <p class="subtitle">Scannez ce QR code pour acceder directement a la boutique en ligne.</p>
              <div class="qr-frame">
                <img src="${qrData}" alt="QR boutique e-commerce" />
              </div>
              <div class="url">${escapeHtml(selectedStoreUrl)}</div>
              <footer class="powered-by">${escapeHtml(QR_PRINT_SIGNATURE)}</footer>
            </main>
            <script>
              window.onload = function() {
                window.focus();
                window.print();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } finally {
      setPrintingQr(false);
    }
  };



  const accessActions = <Space wrap><Button disabled={!selectedStoreUrl} icon={<CopyOutlined />} onClick={() => void copyToClipboard(selectedStoreUrl, "Lien boutique copié.")}>Copier le lien</Button>
    <Button disabled={!selectedStoreUrl} icon={<QrcodeOutlined />} onClick={() => setQrOpen(true)}>Afficher le QR</Button></Space>;
  const accessPanel = <section className="storefront-access-panel" aria-label="Lien et QR boutique"><h2>Lien & QR boutique</h2>
    <p>Partagez le lien de votre boutique avec vos clients.</p><div className="storefront-access-link"><Input readOnly aria-label="Lien public de la boutique" value={selectedStoreUrl} prefix={<LinkOutlined />} />{accessActions}</div></section>;

  let emptyMessage = "";
  if (!permissions.canViewFrontend()) emptyMessage = "Vous n'avez pas accès aux paramètres frontend.";
  else if (!selectedCompany || !selectedWarehouse) emptyMessage = "Sélectionnez une entreprise et un établissement pour configurer la boutique.";
  else if (!permissions.canViewStores()) emptyMessage = "La consultation de cette configuration nécessite l'accès aux boutiques.";
  else if (stores.data && !stores.data.length) emptyMessage = "Aucune boutique configurée dans cet établissement. Créez une boutique depuis Configuration boutique.";

  return <div className="storefront-settings">
    {modalContext}{noticeContext}
    <div className="storefront-mobile-header"><MobileModuleHeader title="Personnaliser la boutique" onMenu={onMenu} /></div>
    <header className="storefront-page-heading"><div><h1>Personnaliser la boutique</h1><p>Réglez l'apparence de votre vitrine et vérifiez le résultat.</p></div>
      <div className="storefront-shop-picker"><label htmlFor="storefront-store">Boutique</label><Select id="storefront-store" aria-label="Boutique" value={selectedStoreId}
        loading={stores.loading && !stores.data} disabled={saving || paymentBusy || !stores.data?.length} onChange={selectStore}
        options={(stores.data || []).map((store) => ({ value: Number(store.id), label: store.store_name }))} />
        <Button icon={<EyeOutlined />} onClick={previewStore} disabled={!selectedStoreUrl}>Ouvrir la boutique</Button></div></header>
    {stores.error && <Alert type="error" showIcon message={stores.data ? "La liste des boutiques n'a pas pu être actualisée." : "Impossible de charger les boutiques."}
      description="Les données déjà chargées et votre saisie sont conservées."
      action={<Button onClick={() => void stores.refresh().catch(() => {})}>Réessayer</Button>} />}
    {emptyMessage ? <Alert type="info" showIcon message={emptyMessage} /> : <>
      {storeMissing && <Alert type="error" showIcon message="Cette boutique n'est plus disponible dans l'établissement." description="Votre saisie est conservée. Sélectionnez une autre boutique pour poursuivre." />}
      {settings.error && <Alert data-testid="storefront-settings-load-error" type="error" showIcon message="Les paramètres n'ont pas pu être actualisés."
        description={loaded ? "Les anciennes données et vos modifications sont conservées. Réessayez avant d'enregistrer." : "Réessayez pour charger les paramètres de cette boutique."}
        action={<Button onClick={() => void settings.refresh().catch(() => {})}>Réessayer</Button>} />}
      {(conflict || recoveryNeeded) && <Alert type="warning" showIcon message={conflict ? "Ces paramètres ont été modifiés depuis une autre session." : "L'état enregistré doit être rechargé."}
        description="Votre saisie est conservée. Rechargez les valeurs enregistrées pour comparer et reprendre vos modifications."
        action={<Button onClick={reloadStoredValues}>Recharger les valeurs enregistrées</Button>} />}
      <div className="storefront-workspace">
        <nav className="storefront-section-nav" aria-label="Rubriques des paramètres de la boutique">{sections.map((section) => <button key={section.key} type="button"
          className={activeSection === section.key ? "is-active" : ""} aria-current={activeSection === section.key ? "page" : undefined}
          onClick={() => changeSection(section.key)}>{section.icon}<span>{section.label}</span>{section.key === "banners" && <span className="storefront-nav-count">{bannerImagesList.length}</span>}</button>)}</nav>
        <div className="storefront-mobile-section"><label htmlFor="storefront-section">Rubrique</label><Select id="storefront-section" value={activeSection} onChange={changeSection}
          options={sections.map((section) => ({ value: section.key, label: section.label }))} /></div>
        <main className="storefront-editor" aria-label="Réglages de la boutique">
          {!loaded && <Skeleton active paragraph={{ rows: 10 }} title={{ width: "50%" }} />}
          <div hidden={!loaded}>
          <Form form={form} layout="vertical" name="storefront-settings" disabled={!canEdit || blocked} onValuesChange={markDirty} onFinish={handleSubmit}
            onFinishFailed={({ errorFields }) => {
              const field = errorFields[0]; if (!field) return;
              changeSection(storefrontFieldSection(String(field.name[0]))); if (field.name[0] === "delivery_zones") setDeliveryZonesExpanded(true);
              window.requestAnimationFrame(() => form.scrollToField(field.name, { block: "center" }));
            }}>
            <section hidden={activeSection !== "identity"} aria-labelledby="storefront-identity-title"><div className="storefront-section-heading"><div><h2 id="storefront-identity-title">Identité visuelle</h2><p>Personnalisez les couleurs, la police et le pied de page de votre boutique.</p></div></div>
              <h3>Couleurs de la boutique</h3><div className="storefront-colors">{colorControl("Couleur principale", "primary_color", "#173f5f")}{colorControl("Couleur secondaire", "secondary_color", "#e63b3b")}</div>
              <h3>En-tête</h3><div className="storefront-colors">{colorControl("Fond de l'en-tête", "header_background_color", "#173f5f")}{colorControl("Éléments de l'en-tête", "header_elements_color", "#ffffff")}</div>
              <h3>Typographie</h3><Form.Item label="Police de caractères" name="font_family"><Select options={Array.from(new Set(["Inter", "Arial", "Helvetica", "Georgia", "Times New Roman", "Roboto", values.font_family].filter(Boolean))).map((value) => ({ value, label: value }))} /></Form.Item>
              <h3>Pied de page</h3><Form.Item label="Texte du pied de page" name="footer_text"><TextArea rows={3} placeholder="Texte personnalisé pour le pied de page" /></Form.Item>
            </section>
            <section hidden={activeSection !== "catalogue"} aria-labelledby="storefront-catalogue-title"><div className="storefront-section-heading"><div><h2 id="storefront-catalogue-title">Expérience catalogue</h2><p>Choisissez les éléments visibles et le comportement du carrousel.</p></div></div>
              <div className="storefront-toggle-grid"><Form.Item label="Afficher la recherche" name="show_search" valuePropName="checked"><Switch /></Form.Item><Form.Item label="Afficher les catégories" name="show_categories" valuePropName="checked"><Switch /></Form.Item></div>
              <Form.Item label="Produits par page" name="products_per_page"><InputNumber min={6} max={48} step={6} /></Form.Item>
              <h3>Carrousel</h3><div className="storefront-toggle-grid"><Form.Item label="Lecture automatique" name="autoplay" valuePropName="checked"><Switch /></Form.Item><Form.Item label="Délai (ms)" name="autoplay_delay"><InputNumber min={1000} step={500} /></Form.Item><Form.Item label="Indicateurs" name="show_dots" valuePropName="checked"><Switch /></Form.Item><Form.Item label="Flèches" name="show_arrows" valuePropName="checked"><Switch /></Form.Item></div>
              <Form.Item label="Effet" name="transition_effect"><Select options={[{ value: "slide", label: "Glissement" }, { value: "fade", label: "Fondu" }, { value: "zoom", label: "Zoom" }]} /></Form.Item>
            </section>
            <div hidden={activeSection !== "banners"}><StorefrontBanners desktop={desktopPreviews} mobile={mobilePreviews} disabled={!canEdit || blocked}
              onPreview={setSelectedBanner} onChange={(desktop, mobile) => { setBannerImagesList(desktop.map(({ thumbUrl, ...entry }) => entry)); setMobileBannersList(mobile.map(({ thumbUrl, ...entry }) => entry)); markDirty(); }} /></div>
            <section hidden={activeSection !== "delivery"} aria-label="Livraison et retours"><div className="storefront-section-heading"><div><h2>Livraison & retours</h2><p>Informations affichées au client pendant son achat.</p></div></div>
              <Alert
                className="ecommerce-admin-inline-alert"
                message="Visibilité côté client"
                description="Le client choisit une zone au checkout. Son tarif est ajouté automatiquement au total de la commande et au paiement en ligne."
                type="info"
                showIcon
              />

              {legacyDeliveryZones && (
                <Alert
                  className="ecommerce-admin-inline-alert"
                  message="Ancienne liste de zones détectée"
                  description={`« ${legacyDeliveryZones} ». Recréez ces zones ci-dessous avec leur tarif ; l'ancien texte n'est plus utilisé pour calculer les commandes.`}
                  type="warning"
                  showIcon
                />
              )}

              <Row gutter={[16, 0]} className="frontend-delivery-grid">
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Politique de livraison"
                    name="delivery_policy"
                    tooltip="Décrivez vos conditions de livraison"
                  >
                    <TextArea
                      rows={4}
                      placeholder="Exemple: Livraison disponible à Ouagadougou sous 24-48h. Livraison offerte à partir de 50 000 FCFA."
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Politique de retour"
                    name="return_policy"
                    tooltip="Conditions de retour et remboursement"
                  >
                    <TextArea
                      rows={4}
                      placeholder="Exemple: Retours acceptés sous 7 jours avec emballage d'origine. Remboursement après validation du contrôle."
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Délais de livraison"
                    name="delivery_timeframe"
                    tooltip="Temps estimé pour la livraison"
                  >
                    <Input placeholder="Exemple: 24 à 48 heures" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Montant minimum de commande (FCFA)"
                    name="min_order_amount"
                    tooltip="Montant minimum requis pour passer commande"
                  >
                    <InputNumber
                      min={0}
                      step={1000}
                      style={{ width: "100%" }}
                      placeholder="0 = Pas de minimum"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                      }
                      parser={(value) =>
                        Number((value || "").replace(/\s|,/g, "")) as any
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Informations sur les frais"
                    name="delivery_cost_info"
                    tooltip="Détails sur les tarifs de livraison"
                  >
                    <TextArea
                      rows={3}
                      placeholder="Exemple: Gratuit au-delà de 50 000 FCFA. Sinon 2 000 FCFA en zone urbaine, 5 000 FCFA hors ville."
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Collapse
                    style={{ marginTop: 8 }}
                    activeKey={
                      deliveryZonesExpanded ? ["delivery-zones"] : []
                    }
                    onChange={(keys) =>
                      setDeliveryZonesExpanded(
                        Array.isArray(keys)
                          ? keys.includes("delivery-zones")
                          : keys === "delivery-zones"
                      )
                    }
                    items={[
                      {
                        key: "delivery-zones",
                        label: (
                          <Space size="small" wrap>
                            <Text strong>Zones et tarifs</Text>
                            <Tag color={deliveryZoneCount > 0 ? "blue" : "default"}>
                              {deliveryZoneCount} zone
                              {deliveryZoneCount !== 1 ? "s" : ""}
                            </Tag>
                            {deliveryZoneCount > 0 && (
                              <Text type="secondary">
                                {activeDeliveryZoneCount} active
                                {activeDeliveryZoneCount !== 1 ? "s" : ""}
                              </Text>
                            )}
                            <Text type="secondary">
                              Cliquer pour gérer
                            </Text>
                          </Space>
                        ),
                        children: (
                          <Form.List name="delivery_zones">
                            {(fields, { add, remove }) => (
                              <Space
                                direction="vertical"
                                size="middle"
                                style={{ width: "100%" }}
                              >
                                {fields.map(
                                  ({ key, name, ...restField }, index) => (
                                    <div
                                      key={key}
                                      className="storefront-delivery-zone"
                                    >
                                      <Form.Item
                                        {...restField}
                                        name={[name, "id"]}
                                        hidden
                                      >
                                        <Input />
                                      </Form.Item>
                                      <Row gutter={[12, 0]} align="middle">
                                        <Col xs={24} md={11}>
                                          <Form.Item
                                            {...restField}
                                            label={`Zone ${index + 1}`}
                                            name={[name, "name"]}
                                            rules={[
                                              {
                                                required: true,
                                                whitespace: true,
                                                message:
                                                  "Le nom de la zone est requis",
                                              },
                                              {
                                                max: 150,
                                                message:
                                                  "150 caractères maximum",
                                              },
                                            ]}
                                          >
                                            <Input placeholder="Exemple : Ouaga 2000, Centre-ville, Zone hors ville" />
                                          </Form.Item>
                                        </Col>
                                        <Col xs={24} md={7}>
                                          <Form.Item
                                            {...restField}
                                            label="Coût de livraison (FCFA)"
                                            name={[name, "delivery_fee"]}
                                            rules={[
                                              {
                                                required: true,
                                                message: "Le coût est requis",
                                              },
                                            ]}
                                          >
                                            <InputNumber
                                              min={0}
                                              precision={2}
                                              step={500}
                                              style={{ width: "100%" }}
                                              formatter={(value) =>
                                                `${value}`.replace(
                                                  /\B(?=(\d{3})+(?!\d))/g,
                                                  " "
                                                )
                                              }
                                              parser={(value) =>
                                                Number(
                                                  (value || "")
                                                    .replace(/\s/g, "")
                                                    .replace(",", ".")
                                                ) as any
                                              }
                                            />
                                          </Form.Item>
                                        </Col>
                                        <Col xs={18} md={4}>
                                          <Form.Item
                                            {...restField}
                                            label="Disponible"
                                            name={[name, "is_active"]}
                                            valuePropName="checked"
                                          >
                                            <Switch />
                                          </Form.Item>
                                        </Col>
                                        <Col xs={6} md={2}>
                                          <Button
                                            danger
                                            type="text"
                                            aria-label={`Supprimer la zone ${
                                              index + 1
                                            }`}
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => remove(name)}
                                          />
                                        </Col>
                                      </Row>
                                    </div>
                                  )
                                )}
                                <Button
                                  type="dashed"
                                  block
                                  icon={<PlusOutlined />}
                                  onClick={() =>
                                    add({
                                      name: "",
                                      delivery_fee: 0,
                                      is_active: true,
                                    })
                                  }
                                >
                                  Ajouter une zone de livraison
                                </Button>
                              </Space>
                            )}
                          </Form.List>
                        ),
                      },
                    ]}
                  />
                </Col>
              </Row>

            </section>
            <section hidden={activeSection !== "social"} aria-labelledby="storefront-social-title"><div className="storefront-section-heading"><div><h2 id="storefront-social-title">Réseaux sociaux</h2><p>Liens de contact visibles par vos clients.</p></div></div>
              {["facebook", "instagram", "twitter"].map((name) => <Form.Item key={name} label={name === "twitter" ? "X / Twitter" : name[0].toUpperCase() + name.slice(1)} name={name}><Input placeholder="https://" /></Form.Item>)}
              <Form.Item label="WhatsApp" name="whatsapp"><Input placeholder="+226 XX XX XX XX" /></Form.Item>
            </section>
            <section hidden={activeSection !== "access"} aria-label="Accès et QR de la boutique"><div className="storefront-section-heading"><div><h2>Accès & QR boutique</h2><p>Ouvrez la vitrine et préparez son QR à imprimer.</p></div></div>
              {selectedStore?.is_active === false && <Alert type="warning" showIcon message="Boutique inactive" description="La boutique doit être active pour être accessible publiquement." />}
              <Input readOnly value={selectedStoreUrl} aria-label="Adresse de la boutique" /><Space wrap><Button disabled={false} icon={<EyeOutlined />} onClick={previewStore}>Ouvrir la boutique</Button>
                <Button disabled={false} icon={<PrinterOutlined />} loading={printingQr} onClick={() => void printStoreQr()}>Imprimer le QR</Button><Button disabled={false} icon={<CopyOutlined />} onClick={() => void copyToClipboard(selectedStoreUrl, "Lien boutique copié.")}>Copier le lien</Button></Space>
              {storeQr && <img className="storefront-access-qr" src={storeQr} alt="QR de la boutique" />}
            </section>
          </Form>
          <div hidden={activeSection !== "payment"} className="storefront-payment">{paymentOpened && <YengaPayConfigCard key={selectedStoreId} storeId={selectedStoreId} active={activeSection === "payment"}
            onDirtyChange={setPaymentDirty} onBusyChange={setPaymentBusy} />}</div>
          </div>
        </main>
        <aside className="storefront-preview-sidebar">{loaded && <StorefrontPreview store={selectedStore} values={values} desktop={desktopPreviews} mobile={mobilePreviews}
          products={catalogue.data || []} catalogueError={Boolean(catalogue.error)} canViewProducts={permissions.canViewProducts()} selectedBanner={selectedBanner} onSelectBanner={setSelectedBanner}
          publicUrl={selectedStoreUrl} onRetry={() => void catalogue.refresh().catch(() => {})} />}{accessPanel}</aside>
      </div>
      {activeSection !== "payment" && <footer className="storefront-save-bar"><div className={dirty ? "storefront-dirty" : "storefront-saved"}>{dirty ? <WarningOutlined /> : <CheckCircleOutlined />}<div><strong>{dirty ? "Modifications non enregistrées" : "Paramètres enregistrés"}</strong>
        <small>{saveReason}</small></div></div>
        <div className="storefront-save-actions"><Button disabled={!dirty || saving} onClick={() => askDiscard(() => baselineRef.current && applyStore(baselineRef.current))}>Annuler les modifications</Button>
          <Button data-testid="storefront-save" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!canEdit || blocked || conflict || !dirty} onClick={() => form.submit()}>Enregistrer les paramètres</Button></div></footer>}
    </>}
    <Modal title="QR de la boutique" open={qrOpen} onCancel={() => setQrOpen(false)} footer={<Button icon={<PrinterOutlined />} loading={printingQr} onClick={() => void printStoreQr()}>Imprimer le QR</Button>}>
      <p>Scannez ce QR pour accéder à {selectedStore?.store_name || "la boutique"}.</p>{selectedStore?.is_active === false && <Alert type="warning" message="Cette boutique est inactive." showIcon />}
      {storeQr ? <img className="storefront-access-qr" src={storeQr} alt="QR de la boutique" /> : <Alert type="info" message="Le QR est en cours de préparation." />}
    </Modal>
  </div>;
}

export default function ParametresBoutique({ initialStoreId }: { initialStoreId?: number } = {}) {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user } = useEcommercePermissions();
  // Remonter le formulaire ferme les anciennes ressources et ignore leurs réponses.
  return <StorefrontWorkspace key={`${user?.id || ""}:${selectedCompany || ""}:${selectedWarehouse || ""}`} initialStoreId={initialStoreId} />;
}
